/**
 * @file UserPicker.tsx
 * @brief User selection control — pill trigger that opens a modal
 *        sheet of pill-shaped user rows. Mirrors `CompanyPicker`.
 *
 * @details
 * - List items come from `useUserOptions(search)` (TanStack Query,
 *   debounced via `useDebouncedValue`). The picker forwards the search
 *   string to the hook (controlled mode of `<PickerSheet>`).
 * - When the parent only stores the user id, `useUser(id)` resolves
 *   the row so the trigger pill always shows a label (full name +
 *   email).
 * - The trigger renders an avatar circle with the user's initials
 *   when no profile image is available.
 *
 * Used by `SessionsFilters` to filter by owner and by future surfaces
 * that need to attach a user (e.g. assignment dialogs).
 *
 * @dependencies
 * - features/users/api (useUser, useUserOptions)
 * - shared/hooks/useDebouncedValue
 * - shared/utils/formatters (formatFullName, getInitials)
 * - components/ui/PickerSheet
 *
 * @author DES
 * @date 26-05-2026
 * @updated 01-10-2026: `variant="pill"`: the page-header `HeaderFilterMenu`
 */

import { useMemo, useState } from "react";
import { ChevronsUpDown, User as UserIcon, Users } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { SearchableMenuList, type SearchableMenuItem } from "@/components/ui/SearchableMenuList";
import { Avatar } from "@/components/ui/Avatar";
import { HeaderFilterMenu } from "@/components/ui/HeaderFilterMenu";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { cn } from "@/shared/theme/cn";
import { formatFullName, getInitials } from "@/shared/utils/formatters";
import { useUser, useUserOptions } from "../api";
import type { AdminUser } from "../schemas";

interface UserPickerProps {
  value?: string | null;
  onChange: (userId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: "sm" | "md";
  /** Optional: scope the picker to a single company (e.g. company-admin
   * surfaces). The hook does not yet support this — kept here so callers
   * can adopt it without API churn when we wire it up. */
  companyId?: string | null;
  /** Extra classes for the trigger button (e.g. flush filter cells). */
  triggerClassName?: string;
  /** `pill`: the 56px page-header filter beside the date circle. */
  variant?: "field" | "pill";
}

const ANY_USER = "__any__";

function userToItem(u: AdminUser): SearchableMenuItem {
  const fullName = formatFullName(u.first_name, u.last_name);
  const label = fullName !== "—" ? fullName : u.email;
  return {
    value: u.id,
    label,
    description: u.email,
    icon: (
      <Avatar
        src={u.profile_image_url}
        initials={getInitials(u.first_name, u.last_name, u.email)}
        alt=""
        size="sm"
      />
    ),
  };
}

export function UserPicker({
  value,
  onChange,
  placeholder = "Select a user…",
  disabled,
  size = "md",
  triggerClassName,
  variant = "field",
}: UserPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 250);

  // ----------------------------------------------------------------------
  // Resolve the currently selected user so the trigger can show the
  // name even when the parent only stored the id.
  // ----------------------------------------------------------------------
  const selected = useUser(value ?? null, Boolean(value));

  // ----------------------------------------------------------------------
  // Fetch options for the open modal — debounced, paged from backend.
  // ----------------------------------------------------------------------
  const options = useUserOptions(debouncedSearch, open);
  const rows = useMemo(() => options.data?.users ?? [], [options.data?.users]);

  const items: SearchableMenuItem[] = useMemo(() => rows.map(userToItem), [rows]);

  const triggerLabel = useMemo(() => {
    if (!value) return null;
    if (selected.data) {
      const full = formatFullName(selected.data.first_name, selected.data.last_name);
      return full !== "—" ? full : selected.data.email;
    }
    const fromList = rows.find((r) => r.id === value);
    if (!fromList) return null;
    const full = formatFullName(fromList.first_name, fromList.last_name);
    return full !== "—" ? full : fromList.email;
  }, [value, selected.data, rows]);

  const handleOpen = (next: boolean) => {
    if (next) setSearch("");
    setOpen(next);
  };

  if (variant === "pill") {
    const picked = selected.data ?? rows.find((r) => r.id === value) ?? null;
    // The "any" row leads the list until a search narrows it.
    const pillItems: SearchableMenuItem[] = search.trim()
      ? items
      : [
          {
            value: ANY_USER,
            label: placeholder,
            icon: (
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand/10 text-brand">
                <Users className="h-3.5 w-3.5" aria-hidden />
              </span>
            ),
          },
          ...items,
        ];
    return (
      <HeaderFilterMenu
        label={triggerLabel ?? (value && selected.isLoading ? "Loading…" : placeholder)}
        active={Boolean(value)}
        onClear={() => onChange(null)}
        icon={
          picked ? (
            <Avatar
              src={picked.profile_image_url}
              initials={getInitials(picked.first_name, picked.last_name, picked.email)}
              alt=""
              size="md"
            />
          ) : (
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand/10 text-brand">
              <UserIcon className="h-4 w-4" />
            </span>
          )
        }
        title="Filter by user"
        searchPlaceholder="Search by name or email"
        items={pillItems}
        loading={options.isFetching}
        disabled={disabled}
        query={search}
        onQueryChange={setSearch}
        onOpenChange={handleOpen}
        noMatchText={`No users match “${debouncedSearch}”.`}
        emptyText="No users yet. Create one from the Users page."
        selected={value ?? ANY_USER}
        onSelect={(id) => onChange(id === ANY_USER ? null : id)}
      />
    );
  }

  const triggerHeight = size === "sm" ? "h-9" : "h-10";

  return (
    // A dropdown under the button, searchable (DES 30-09-2026: "ab user
    // picker aisa banao"), in place of the side sheet.
    <DropdownMenu open={open} onOpenChange={handleOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex w-full items-center justify-between rounded-full border border-input bg-field px-5 text-sm transition-colors",
            triggerHeight,
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            !triggerLabel && "text-muted-foreground",
            triggerClassName,
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            <UserIcon className="h-4 w-4 shrink-0 opacity-60" />
            <span className="truncate">
              {triggerLabel ?? (value && selected.isLoading ? "Loading…" : placeholder)}
            </span>
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-72 p-1.5"
      >
        <SearchableMenuList
          title="Select user"
          searchPlaceholder="Search by name or email"
          items={items}
          loading={options.isFetching}
          query={search}
          onQueryChange={setSearch}
          noMatchText={`No users match “${debouncedSearch}”.`}
          emptyText="No users yet. Create one from the Users page."
          selected={value ? [value] : []}
          onSelect={(id) => onChange(id)}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
